import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosInstance } from "./AxiosInstance";

export const useRegister = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/register',data);
            return response.data;
        }
    })
}

export const useLogin = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/login',data);
            return response.data;
        }
    })
}

export const useResendActivationCode = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/resend-activation', data);
            return response.data;
        }
    })
}

export const useActivateAccount = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/activate', data);
            return response.data;
        }
    })
}

export const useForgotPassword = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/forgot-password', data);
            return response.data;
        }
    })
}

export const useResetPassword = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/reset-password', data);
            return response.data;
        }
    })
}

export const useVerify2FALogin = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/verify-2fa', data);
            return response.data;
        }
    })
}

export const useRequestAdminActivation = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/admin/request-activation', data);
            return response.data;
        }
    })
}

export const useVerifyAdminActivationCode = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/admin/verify-activation', data);
            return response.data;
        }
    })
}

export const useCompleteAdminActivation = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/admin/complete-activation', data);
            return response.data;
        }
    })
}

export const useCreateAdmin = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/auth/admin/create', data);
            return response.data;
        }
    })
}

export const useGetAdmins = () => {
    return useMutation({
        mutationFn: async () => {
            const response = await AxiosInstance.get('/admins/manage');
            return response.data;
        }
    })
}

export const useCreateHellocian = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/hellocians/create', data);
            return response.data;
        }
    })
}

export const useGetHellocians = () => {
    return useMutation({
        mutationFn: async () => {
            const response = await AxiosInstance.get('/hellocians');
            return response.data;
        }
    })
}

export const useSetupHellocianPassword = () => {
    return useMutation({
        mutationFn: async (data) => {
            const response = await AxiosInstance.post('/hellocians/setup-password', data);
            return response.data;
        }
    })
}

export const useToggleBlockHellocian = () => {
    return useMutation({
        mutationFn: async ({ id, reason }) => {
            const response = await AxiosInstance.patch(`/hellocians/${id}/toggle-block`, { reason });
            return response.data;
        }
    })
}

export const useDeleteHellocian = () => {
    return useMutation({
        mutationFn: async (id) => {
            const response = await AxiosInstance.delete(`/hellocians/${id}`);
            return response.data;
        }
    })
}

export const useGetTrashedHellocians = () => {
    return useMutation({
        mutationFn: async () => {
            const response = await AxiosInstance.get('/hellocians/trash');
            return response.data;
        }
    })
}

export const useRestoreHellocian = () => {
    return useMutation({
        mutationFn: async (id) => {
            const response = await AxiosInstance.patch(`/hellocians/${id}/restore`);
            return response.data;
        }
    })
}

export const useHardDeleteHellocian = () => {
    return useMutation({
        mutationFn: async (id) => {
            const response = await AxiosInstance.delete(`/hellocians/${id}/hard`);
            return response.data;
        }
    })
}

export const useBulkDeleteHellocians = () => {
    return useMutation({
        mutationFn: async ({ ids, action }) => {
            const response = await AxiosInstance.post('/hellocians/bulk-delete', { ids, action });
            return response.data;
        }
    })
}

// -----------------------------------------------------------------------------
// SECURE ADMIN MANAGEMENT ENDPOINTS (SUPER-ADMIN ONLY)
// -----------------------------------------------------------------------------

export const useGetTrashedAdmins = () => {
    return useMutation({
        mutationFn: async () => {
            const response = await AxiosInstance.get('/admins/manage/trash');
            return response.data;
        }
    })
}

export const useToggleBlockAdmin = () => {
    return useMutation({
        mutationFn: async ({ id, reason }) => {
            const response = await AxiosInstance.patch(`/admins/manage/${id}/toggle-block`, { reason });
            return response.data;
        }
    })
}

export const useDeleteAdmin = () => {
    return useMutation({
        mutationFn: async (id) => {
            const response = await AxiosInstance.delete(`/admins/manage/${id}`);
            return response.data;
        }
    })
}

export const useRestoreAdmin = () => {
    return useMutation({
        mutationFn: async (id) => {
            const response = await AxiosInstance.patch(`/admins/manage/${id}/restore`);
            return response.data;
        }
    })
}

export const useHardDeleteAdmin = () => {
    return useMutation({
        mutationFn: async (id) => {
            const response = await AxiosInstance.delete(`/admins/manage/${id}/hard`);
            return response.data;
        }
    })
}

export const useBulkDeleteAdmins = () => {
    return useMutation({
        mutationFn: async ({ ids, action }) => {
            const response = await AxiosInstance.post('/admins/manage/bulk-delete', { ids, action });
            return response.data;
        }
    })
}

// -----------------------------------------------------------------------------
// CLIENT MANAGEMENT ENDPOINTS
// -----------------------------------------------------------------------------

export const useGetClients = () => {
    return useMutation({
        mutationFn: async () => {
            const response = await AxiosInstance.get('/clients');
            return response.data;
        }
    })
}

export const useGetTrashedClients = () => {
    return useMutation({
        mutationFn: async () => {
            const response = await AxiosInstance.get('/clients/trash');
            return response.data;
        }
    })
}

export const useToggleBlockClient = () => {
    return useMutation({
        mutationFn: async ({ id, reason }) => {
            const response = await AxiosInstance.patch(`/clients/${id}/toggle-block`, { reason });
            return response.data;
        }
    })
}

export const useDeleteClient = () => {
    return useMutation({
        mutationFn: async (id) => {
            const response = await AxiosInstance.delete(`/clients/${id}`);
            return response.data;
        }
    })
}

export const useRestoreClient = () => {
    return useMutation({
        mutationFn: async (id) => {
            const response = await AxiosInstance.patch(`/clients/${id}/restore`);
            return response.data;
        }
    })
}

export const useHardDeleteClient = () => {
    return useMutation({
        mutationFn: async (id) => {
            const response = await AxiosInstance.delete(`/clients/${id}/hard`);
            return response.data;
        }
    })
}

export const useBulkDeleteClients = () => {
    return useMutation({
        mutationFn: async ({ ids, action }) => {
            const response = await AxiosInstance.post('/clients/bulk-delete', { ids, action });
            return response.data;
        }
    })
}
export const useGetMe = () => {
    return useQuery({
        queryKey: ['me'],
        queryFn: async () => {
            const response = await AxiosInstance.get('/auth/me');
            return response.data.user;
        }
    })
}
